/**
 * Barre latérale — navigation principale du tableau de pilotage.
 *
 * Quatre groupes, dans l'ordre où on lit l'entreprise : la synthèse, ce qui
 * rentre (Shops), ce qui sort (Spends), et les clients (Customers).
 *
 * Les liens gardent la query string : passer de Shopify à Ads ne doit pas
 * réinitialiser la période choisie.
 */

import { roleAllowsPage } from '@shared/types/auth';
import { NavLink, useLocation } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';

import { NAV_ICONS, type NavIconName } from './NavIcons';

interface NavItem {
  to: string;
  label: string;
  icon: NavIconName;
  /** Actif aussi sur les sous-pages (Analytics et ses onglets). */
  prefix?: boolean;
}

interface NavGroup {
  title?: string;
  items: readonly NavItem[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    items: [
      { to: '/', label: 'Résumé', icon: 'summary' },
      { to: '/marqueurs', label: 'Marqueurs', icon: 'markers' },
    ],
  },
  {
    title: 'Campagnes',
    items: [{ to: '/bf26', label: 'BF 26', icon: 'campaign' }],
  },
  {
    title: 'Shops',
    items: [
      { to: '/shopify', label: 'Shopify', icon: 'shopify' },
      { to: '/souscriptions', label: 'Souscriptions', icon: 'subscriptions' },
      { to: '/amazon', label: 'Amazon', icon: 'amazon' },
      { to: '/btob', label: 'BtoB', icon: 'btob' },
    ],
  },
  {
    title: 'Spends',
    items: [
      { to: '/ads', label: 'Ads', icon: 'ads' },
      { to: '/frais', label: 'Frais', icon: 'fees' },
      { to: '/charges', label: 'Charges', icon: 'charges' },
    ],
  },
  {
    title: 'Customers',
    items: [
      { to: '/analytics', label: 'Analytics', icon: 'analytics' },
      { to: '/usage', label: 'Usage app', icon: 'usage', prefix: true },
      { to: '/sav', label: 'SAV', icon: 'support' },
    ],
  },
];

interface SidebarProps {
  /** Mobile : barre ouverte par-dessus la page. */
  open: boolean;
  /** Compte connecté — sur téléphone, l'entrée « Compte » du tiroir remplace l'email de l'en-tête. */
  email: string | null;
  /** Appelé au clic sur un lien — referme la barre sur mobile. */
  onNavigate: () => void;
  /** Sur téléphone, la déconnexion vit en bas du tiroir : l'en-tête garde la place du bouton recharger. */
  onSignOut: () => void;
}

export function Sidebar({ open, email, onNavigate, onSignOut }: SidebarProps) {
  const { search } = useLocation();
  const { role } = useAuth();
  // Rôle `user` : seulement ses pages, et les groupes qui en gardent une.
  const groups = NAV_GROUPS.map((group) => ({ ...group, items: group.items.filter((item) => role !== null && roleAllowsPage(role, item.to)) })).filter(
    (group) => group.items.length > 0,
  );

  return (
    <nav className={open ? 'sidebar is-open' : 'sidebar'} aria-label="Navigation principale">
      <div className="sidebar__brand">
        <span className="app-header__dot" aria-hidden="true" />
        Piano LED
      </div>

      {groups.map((group, index) => (
        <div className="sidebar__group" key={group.title ?? index}>
          {group.title && <p className="sidebar__title">{group.title}</p>}
          <ul className="sidebar__list">
            {group.items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={{ pathname: item.to, search }}
                  // `end` : « Résumé » (/) ne doit pas rester actif sur toutes
                  // les pages. Analytics, lui, couvre ses onglets.
                  end={!item.prefix}
                  className={({ isActive }) => (isActive ? 'sidebar__link is-active' : 'sidebar__link')}
                  onClick={onNavigate}
                >
                  {NAV_ICONS[item.icon]}
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {email && (
        <div className="sidebar__group sidebar__group--mobile">
          <ul className="sidebar__list">
            <li>
              <NavLink
                to={{ pathname: '/compte', search }}
                className={({ isActive }) => (isActive ? 'sidebar__link is-active' : 'sidebar__link')}
                onClick={onNavigate}
              >
                {NAV_ICONS.account}
                Compte
              </NavLink>
              <span className="sidebar__email">{email}</span>
            </li>
            <li>
              <button type="button" className="sidebar__link sidebar__signout" onClick={onSignOut}>
                {NAV_ICONS.signout}
                Déconnexion
              </button>
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
}
